import { useRef } from "react";
import type React from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import type { BuildNumbers } from "./types";
import { elapsedAt, useSmoothProgress } from "./useSmoothProgress";
import { stageText } from "@/lib/corrido-status-text";

// BuildProgress — the ONE progress bar every corrido build shows, wherever it
// is running: the chat panel, the board's in-flight card, the storyboard being
// drawn, the canvas loading card. It draws a percent that moves continuously
// (useSmoothProgress), says in plain words what is happening and how many
// pictures or clips exist, counts the time left down between polls, shows how
// long it has been running, and tells the customer they can leave.
//
// Fed straight from GET /api/web/corrido/status. Works with an older server
// that sends `progress` alone: the bar still creeps and the extra lines hide.

export type BuildUnit = "pictures" | "clips";

const num = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);

/** Width (%) of the fill at 0 % — enough to see the bar is there. */
const FILL_NUB = 1.5;

/**
 * The finish phases that count scenes one by one (services/corrido-v2 PHASE_TEXT
 * / progress.js FINISH_PHASES): the count line follows the phase the bar is
 * moving through, so "36 of 36 clips ready" never sits beside a percent that is
 * climbing through the sharpening pass (2026-10-03: an hour of it).
 */
const PHASE_COUNT_KEY: Record<string, string> = {
  checking: "buildProgress.checking",
  finishing: "buildProgress.sharpening",
  replacing: "buildProgress.replacing",
};

/** What an op produces, so the count line says pictures or clips. */
export function unitForOp(op: string | null | undefined): BuildUnit | null | undefined {
  switch (op) {
    case "generate_images":
    case "regen_images":
      return "pictures";
    case "animate_clips":
    case "reroll_clips":
      return "clips";
    case "assemble_video":
      return null; // a cut has no count worth showing
    default:
      return undefined; // unknown op — the caller's fallback decides
  }
}

export type TimeLeft =
  | { kind: "none" }
  | { kind: "overdue" }
  | { kind: "underMinute" }
  | { kind: "minutes"; count: number }
  | { kind: "hours"; count: number };

/**
 * Time left at `now`, counted down locally from the last poll. `eta_ms` is the
 * server's estimate when it answered; without it, expected_total − elapsed.
 * Past the expected total with under a minute to go (the server keeps a small
 * positive eta then), or at zero, it is "almost there", never a negative clock.
 */
export function timeLeftOf(n: BuildNumbers | null | undefined, receivedAt: number, now: number): TimeLeft {
  if (!n || n.status === "completed") return { kind: "none" };
  const since = Math.max(0, now - receivedAt);
  const eta = num(n.eta_ms);
  const expected = num(n.expected_total_ms);
  const elapsed = num(n.elapsed_ms);
  let atReceipt: number | null = null;
  if (eta !== null) atReceipt = Math.max(0, eta);
  else if (expected !== null && expected > 0 && elapsed !== null) atReceipt = expected - elapsed;
  if (atReceipt === null) return { kind: "none" };
  const remaining = atReceipt - since;
  const pastExpected = expected !== null && expected > 0 && elapsed !== null && elapsed + since > expected;
  if (remaining <= 0) return { kind: "overdue" };
  if (remaining < 60_000) return pastExpected ? { kind: "overdue" } : { kind: "underMinute" };
  if (remaining < 90 * 60_000) return { kind: "minutes", count: Math.max(1, Math.round(remaining / 60_000)) };
  return { kind: "hours", count: Math.round(remaining / 3_600_000) };
}

export function timeLeftText(t: TFunction, left: TimeLeft): string {
  switch (left.kind) {
    case "overdue": return t("buildProgress.timeLeft.overdue");
    case "underMinute": return t("buildProgress.timeLeft.underMinute");
    case "minutes": return t("buildProgress.timeLeft.minutes", { count: left.count });
    case "hours": return t("buildProgress.timeLeft.hours", { count: left.count });
    default: return "";
  }
}

/** "4m 12s" / "1h 06m" — elapsed time is the one number a build can never fake. */
export function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  if (m > 0) return `${m}m ${String(s).padStart(2, "0")}s`;
  return `${s}s`;
}

export type BuildProgressProps = {
  /** Identity of the build — every bar with the same key draws the same percent. */
  jobKey: string;
  /** The latest status frame (null before the first poll answers). */
  numbers: BuildNumbers | null | undefined;
  /** Local ms the frame arrived. */
  receivedAt: number;
  /** The line to show instead of the stage text (the chat passes its own note). */
  label?: string | null;
  /** Count unit when the frame names no op. */
  unit?: BuildUnit | null;
  /** Hide the stage line (a caller that already prints it right above). */
  hideLabel?: boolean;
  showLeaveNote?: boolean;
  compact?: boolean;
  testId?: string;
  style?: React.CSSProperties;
};

const CSS = `
@keyframes bp-shimmer{from{background-position:200% 0}to{background-position:-200% 0}}
@keyframes bp-pulse{0%,100%{opacity:.55}50%{opacity:1}}
.bp-shimmer{animation:bp-shimmer 1.6s linear infinite}
.bp-dot{animation:bp-pulse 1.4s ease-in-out infinite}
@media (prefers-reduced-motion: reduce){.bp-shimmer,.bp-dot{animation:none}}
`;

export default function BuildProgress({
  jobKey, numbers, receivedAt, label, unit = null, hideLabel = false, showLeaveNote = true, compact = false,
  testId = "build-progress", style,
}: BuildProgressProps) {
  const { t } = useTranslation();
  // A frame with no arrival time (seeded from a list row) counts from when this
  // bar first appeared — never from the epoch.
  const mountedAt = useRef(Date.now()).current;
  const at = receivedAt > 0 ? receivedAt : mountedAt;
  const { value, now, localStart } = useSmoothProgress(jobKey, numbers, at);
  const completed = numbers?.status === "completed";
  // A fresh frame shows at once (never below the server), even between ticks.
  const drawn = completed ? 100 : Math.max(value, Math.min(100, Math.max(0, num(numbers?.progress) ?? 0)));
  const pct = completed ? 100 : Math.min(99, Math.floor(drawn));

  const line = completed
    ? t("buildProgress.done")
    : (label && label.trim()) || stageText(t, numbers || {}) || t("buildProgress.starting");

  const opUnit = unitForOp(numbers?.op);
  const countUnit = opUnit === undefined ? unit : opUnit;
  const total = num(numbers?.expected_output_count);
  const done = Math.max(0, Math.min(total ?? 0, num(numbers?.completed_output_count) ?? 0));
  const phaseKey = typeof numbers?.phase === "string" ? PHASE_COUNT_KEY[numbers.phase] : undefined;
  const phaseTotal = num(numbers?.phase_total);
  const phaseDone = Math.max(0, Math.min(phaseTotal ?? 0, num(numbers?.phase_done) ?? 0));
  const countText = phaseKey && phaseTotal && phaseTotal > 0
    ? t(phaseKey, { done: phaseDone, count: phaseTotal })
    : countUnit && total && total > 0
      ? t(countUnit === "pictures" ? "buildProgress.pictures" : "buildProgress.clips", { done, count: total })
      : "";

  const left = timeLeftOf(numbers, at, now);
  const leftText = timeLeftText(t, left);
  // Never a clock that runs backwards: the server counts from the job's start,
  // but this bar (and the pending one it took over from) has been on screen
  // since the files started uploading — the first frame used to reset
  // "Running for 40s" to "Running for 2s", which reads as a restart.
  const elapsed = Math.max(
    elapsedAt({ elapsedMs: num(numbers?.elapsed_ms), receivedAt: at, localStart }, now),
    now - localStart,
  );

  const fs = compact ? 11 : 12;
  return (
    <div data-testid={testId} data-progress={pct} style={{ display: "grid", gap: compact ? 5 : 7, ...style }}>
      <style>{CSS}</style>
      {!hideLabel && (
        <div style={{ alignItems: "center", display: "flex", gap: 8, justifyContent: "space-between" }}>
          <div data-testid={`${testId}-label`} style={{ alignItems: "center", color: "#e2e8f0", display: "flex", fontSize: compact ? 12 : 13, fontWeight: 700, gap: 7, minWidth: 0 }}>
            {!completed && <span className="bp-dot" aria-hidden style={{ background: "#38bdf8", borderRadius: "50%", flexShrink: 0, height: 7, width: 7 }} />}
            <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{line}</span>
          </div>
          <span data-testid={`${testId}-percent`} style={{ color: "#e2e8f0", flexShrink: 0, fontSize: compact ? 12 : 13, fontVariantNumeric: "tabular-nums", fontWeight: 800 }}>{pct}%</span>
        </div>
      )}
      <div
        role="progressbar" aria-label={t("buildProgress.ariaLabel")} aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}
        style={{ background: "rgba(255,255,255,.08)", borderRadius: 999, height: compact ? 6 : 8, overflow: "hidden", position: "relative" }}
      >
        <div
          data-testid={`${testId}-fill`}
          data-drawn={drawn.toFixed(3)}
          style={{
            background: completed ? "#34d399" : "linear-gradient(90deg,#38bdf8,#0ea5e9)",
            borderRadius: 999, height: "100%", position: "relative",
            // The drawn percent rides ON TOP of a small visible nub: a floor of
            // max(nub, drawn) hid every move below the nub, so the first half
            // minute of a build (uploading, 0-3 %) showed a bar that never moved.
            transition: "width 300ms linear", width: `${completed ? 100 : FILL_NUB + (drawn * (100 - FILL_NUB)) / 100}%`,
          }}
        >
          {!completed && (
            <span
              className="bp-shimmer" aria-hidden
              style={{
                background: "linear-gradient(90deg,transparent 0%,rgba(255,255,255,.38) 50%,transparent 100%)",
                backgroundSize: "200% 100%", inset: 0, position: "absolute",
              }}
            />
          )}
        </div>
      </div>
      {hideLabel && (
        <span data-testid={`${testId}-percent`} style={{ alignSelf: "end", color: "#94a3b8", fontSize: fs, fontVariantNumeric: "tabular-nums", justifySelf: "end", marginTop: -2 }}>{pct}%</span>
      )}
      {(countText || leftText) && (
        <div style={{ color: "#b6c2d4", display: "flex", flexWrap: "wrap", fontSize: fs, gap: "2px 10px" }}>
          {countText && <span data-testid={`${testId}-count`} style={{ color: "#e2e8f0", fontWeight: 700 }}>{countText}</span>}
          {leftText && <span data-testid={`${testId}-eta`}>{leftText}</span>}
        </div>
      )}
      {!completed && (
        <div style={{ color: "#7c8aa0", display: "flex", flexWrap: "wrap", fontSize: compact ? 10.5 : 11.5, gap: "2px 10px" }}>
          <span data-testid={`${testId}-elapsed`} style={{ fontVariantNumeric: "tabular-nums" }}>{t("buildProgress.elapsed", { elapsed: formatElapsed(elapsed) })}</span>
          {showLeaveNote && <span data-testid={`${testId}-leave`}>{t("buildProgress.leaveNote")}</span>}
        </div>
      )}
    </div>
  );
}
